/**
 * How a team's name is decided.
 *
 * A team name is an admin's OVERRIDE and nothing else. When there is no
 * override the API serves a name DERIVED from the line-up, and the entity's
 * stored name stays null.
 *
 * The two must never be confused on the way back. Sending a derived name to the
 * API persists it as a real override, so the team keeps the name of whoever it
 * was derived from even after the line-up changes. In doubles and up that reads
 * as "the team is named after the first player added", because the derived name
 * leads with one player.
 *
 * Where the override lives depends on the entity, which is the trap this
 * composable exists to close:
 *   - ladder team / league cycle division team -> `actualName` (`name` is derived)
 *   - tournament team -> `name` on the TOURNAMENT team row (its nested `team`
 *     is a plain team payload with a derived `name` and NO `actualName`)
 *
 * Mirrored by the Flutter app's `team_name.dart` — change both together.
 */
export function useTeamName() {
  /**
   * The override to SEND, or null when the admin hasn't named the team.
   * Anything blank is "not named", never an empty-string name.
   */
  const teamNameOverride = (typed) => {
    const trimmed = (typed ?? '').trim();
    return trimmed === '' ? null : trimmed;
  };

  /**
   * The name to SHOW for a team with no override.
   *
   * Mirrors the API's `TeamListResponse.GenerateDetails`: one player is named
   * outright, a pair is joined, and three or more count the rest. Players are
   * ordered by full name, as the API orders them, so both ends produce the same
   * string for the same team.
   *
   * Display only. It must never be sent back as a name.
   */
  const derivedTeamName = (players) => {
    const names = (players ?? [])
        .map(p => p?.fullName || `${p?.firstName ?? ''} ${p?.lastName ?? ''}`.trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));

    if (names.length === 0) return '';
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} + ${names[1]}`;
    return `${names[0]} + ${names.length - 1}`;
  };

  /**
   * The name to SHOW: the admin's override when there is one, otherwise the
   * name derived from the line-up.
   */
  const teamDisplayName = (override, players) =>
      teamNameOverride(override) ?? derivedTeamName(players);

  /**
   * A tournament team's override, read from the row that actually stores it.
   *
   * The nested `team` is a plain team payload: its `name` is the derived
   * display name and it has no `actualName` at all, so reading the override
   * from there silently yields undefined — the name never loads into the
   * editor, and the next save writes null over it.
   */
  const tournamentTeamOverride = (tournamentTeam) =>
      teamNameOverride(tournamentTeam?.name);

  return {teamNameOverride, derivedTeamName, teamDisplayName, tournamentTeamOverride};
}
